import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { Avatar, EmptyState, Loader, PageTitle, StatusBadge } from "@/components/common";

export const MemberRow = ({ m }) => (
  <Link to={`/app/reseau/${m.id}`} data-testid="network-member-row" className="mr-card flex items-center gap-4 p-4 hover:border-mr-text2 transition-colors">
    <Avatar name={m.name} size={44} />
    <div className="min-w-0 flex-1">
      <p className="font-medium truncate">{m.name}</p>
      <p className="text-sm text-mr-text2 truncate">{m.company} · {m.city}</p>
      <p className="text-xs text-mr-muted truncate mt-0.5">{m.sectors.join(", ")}</p>
    </div>
    {m.badges?.length > 0 && <span className="hidden sm:inline text-xs text-mr-accent">{m.badges[0]}</span>}
  </Link>
);

const EMPTY = { sector: "", category: "", city: "", country: "", badge: "", brings: "", seeks: "" };

function Directory() {
  const { settings } = useConfig();
  const [q, setQ] = useState("");
  const [f, setF] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const load = useCallback(async (skip = 0) => {
    const { data: res } = await api.get("/members", { params: { q, ...f, skip } });
    setData((prev) => (skip && prev ? { ...res, items: [...prev.items, ...res.items] } : res));
  }, [q, f]);
  useEffect(() => {
    const t = setTimeout(() => load(0).catch(() => setData({ items: [], total: 0 })), 300);
    return () => clearTimeout(t);
  }, [load]);
  const sel = (k, label, key) => (
    <label className="block"><span className="mr-label">{label}</span><select data-testid={`network-filter-${k}`} className="mr-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}>
      <option value="">Tous</option>{(settings[key] || []).map((o) => <option key={o}>{o}</option>)}</select></label>
  );
  const active = Object.values(f).filter(Boolean).length;
  return (
    <>
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mr-muted" />
          <input data-testid="network-search-input" className="mr-input pl-10" placeholder="Nom, entreprise, produit…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button data-testid="network-filters-toggle" className="mr-btn-secondary px-3" onClick={() => setOpen(!open)}><SlidersHorizontal size={16} />{active ? ` ${active}` : ""}</button>
      </div>
      {open && (
        <div className="mr-card grid sm:grid-cols-2 gap-3 mb-4" data-testid="network-filters">
          {sel("sector", "Secteur", "catalog.sectors")}
          {sel("category", "Catégorie de produits", "catalog.product_categories")}
          {sel("city", "Ville", "catalog.cities")}
          {sel("country", "Pays d'approvisionnement", "catalog.countries")}
          {sel("badge", "Badge", "catalog.badges")}
          <label className="block"><span className="mr-label">Ce qu'il apporte</span><input data-testid="network-filter-brings" className="mr-input" value={f.brings} onChange={(e) => setF({ ...f, brings: e.target.value })} /></label>
          <label className="block"><span className="mr-label">Ce qu'il cherche</span><input data-testid="network-filter-seeks" className="mr-input" value={f.seeks} onChange={(e) => setF({ ...f, seeks: e.target.value })} /></label>
          <button data-testid="network-filters-reset" className="mr-btn-ghost self-end" onClick={() => setF(EMPTY)}>Réinitialiser</button>
        </div>
      )}
      {data === null ? <Loader rows={5} /> : data.items.length === 0 ? (
        <EmptyState icon={Users} title="Aucun membre trouvé" text="Essayez un autre mot-clé ou d'autres filtres." testId="network-empty" />
      ) : (
        <>
          <p className="text-xs text-mr-muted mb-3 tabular" data-testid="network-total">{data.total} membre{data.total > 1 ? "s" : ""}</p>
          <div className="space-y-2">{data.items.map((m) => <MemberRow key={m.id} m={m} />)}</div>
          {data.items.length < data.total && <button data-testid="network-load-more-button" className="mr-btn-secondary w-full mt-4" onClick={() => load(data.items.length)}>Afficher plus</button>}
        </>
      )}
    </>
  );
}

function Introductions() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/me/introductions").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);
  const respond = async (id, accept) => {
    try {
      await api.post(`/introductions/${id}/respond`, { accept });
      toast.success(accept ? "Réponse enregistrée" : "Mise en relation déclinée");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!items) return <Loader />;
  if (!items.length) return <EmptyState icon={Users} title="Aucune mise en relation" text="Depuis le profil d'un membre, demandez à être présenté ou présentez deux membres." testId="intro-empty" />;
  return (
    <ul className="space-y-2">{items.map((i) => (
      <li key={i.id} className="mr-card p-4 space-y-2" data-testid="intro-row">
        <div className="flex justify-between gap-3"><p className="text-sm"><strong>{i.introducer_name}</strong> présente <strong>{i.requester_name}</strong> à <strong>{i.target_name}</strong></p><StatusBadge status={i.status} /></div>
        {i.message && <p className="text-sm text-mr-text2">{i.message}</p>}
        {i.can_respond && <div className="flex gap-2"><button data-testid="intro-accept-button" className="mr-btn-primary px-3" onClick={() => respond(i.id, true)}>Accepter</button><button data-testid="intro-decline-button" className="mr-btn-ghost px-3" onClick={() => respond(i.id, false)}>Décliner</button></div>}
        {i.conversation_id && <Link to={`/app/messages/${i.conversation_id}`} className="mr-link text-sm" data-testid="intro-thread-link">Ouvrir le fil à trois</Link>}
      </li>))}</ul>
  );
}

export default function Network() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "annuaire";
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Réseau" title="Annuaire des membres" />
      <div className="flex gap-1 mb-5 border-b border-mr-border">
        {[["annuaire", "Annuaire"], ["relations", "Mises en relation"]].map(([id, l]) => (
          <button key={id} data-testid={`network-tab-${id}`} onClick={() => setParams({ tab: id })} className={`px-3 min-h-[44px] text-sm font-medium border-b-2 -mb-px ${tab === id ? "border-mr-accent text-mr-text" : "border-transparent text-mr-muted"}`}>{l}</button>))}
      </div>
      {tab === "relations" ? <Introductions /> : <Directory />}
    </div>
  );
}
